import { useAtom, useAtomValue } from 'jotai';
import { Device } from '../helpers/types';
import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { fetch as fetchService } from '../services/devicesService';
import { devicesAtom } from '../atoms/devicesAtom';
import { userAtom } from '../atoms/userAtom';

interface DevicesContextType {
  devices: Device[] | null;
  fetchDevices: (username: string) => Promise<void>;
  isLoading: boolean;
}

const DevicesContext = createContext<DevicesContextType | undefined>(undefined);

export const DevicesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [devices, setDevices] = useAtom(devicesAtom);
  const [isLoading, setIsLoading] = useState(true);
  const user = useAtomValue(userAtom);     

  const fetchDevices = useCallback(async (username: string) => {
    setIsLoading(true);
    try {
      const { devices } = await fetchService(username);
      setDevices(devices);
    } catch (error) {
      console.error("Failed to fetch devices:", error);
    } finally {
      setIsLoading(false);
    }
  }, [setDevices]);

  useEffect(() => {
  
    if (!user?.id) {
      setDevices(null);
      setIsLoading(false);
      return;
    }
    fetchDevices(user.username);
  }, [user, fetchDevices, setDevices]);

  return (
    <DevicesContext.Provider value={{ devices, fetchDevices, isLoading }}>
      {children}
    </DevicesContext.Provider>
  );
};

export const useDevices = () => {
  const context = useContext(DevicesContext);
  if (!context) {
    throw new Error('useDevices must be used within a DevicesProvider');
  }
  return context;
};
